import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { useClueSuggestions } from '@/hooks/useClueSuggestions';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';

interface ClueSuggestionFormProps {
  prefillWord?: string;
}

export function ClueSuggestionForm({ prefillWord }: ClueSuggestionFormProps) {
  const [word, setWord] = useState(prefillWord || '');
  const [clue, setClue] = useState('');
  const [wordExists, setWordExists] = useState<boolean | null>(null);
  const [checkingWord, setCheckingWord] = useState(false);
  
  const { submitClue, checkWordExists, loading } = useClueSuggestions();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (prefillWord) {
      setWord(prefillWord);
    }
  }, [prefillWord]);

  useEffect(() => {
    if (word.length < 2) {
      setWordExists(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingWord(true);
      const exists = await checkWordExists(word);
      setWordExists(exists);
      setCheckingWord(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [word, checkWordExists]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!word.trim() || !clue.trim()) {
      toast({
        title: 'Fyll i alla fält',
        description: 'Både ord och ledtråd måste fyllas i.',
        variant: 'destructive',
      });
      return;
    }

    const result = await submitClue(word, clue);
    
    if (result.error) {
      toast({
        title: 'Fel',
        description: result.error,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Förslag skickat!',
        description: 'Din ledtråd kommer att granskas av en administratör.',
      });
      setClue('');
      if (!prefillWord) {
        setWord('');
        setWordExists(null);
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Föreslå ledtråd</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            <Link to="/auth" className="text-primary hover:underline">Logga in</Link> för att föreslå ledtrådar.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <LinkIcon className="w-5 h-5" />
          Föreslå ord och ledtråd
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="word">Ord</Label>
            <div className="relative">
              <Input
                id="word"
                value={word}
                onChange={(e) => setWord(e.target.value.toUpperCase())}
                placeholder="T.ex. SOMMAR"
                className="pr-10"
                disabled={!!prefillWord}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checkingWord && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                {!checkingWord && wordExists === true && <CheckCircle className="w-4 h-4 text-green-500" />}
                {!checkingWord && wordExists === false && word.length >= 2 && (
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                )}
              </div>
            </div>
            {wordExists === false && word.length >= 2 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Ordet finns inte ännu – det kommer att skapas vid godkännande.
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="clue">Ledtråd/synonym</Label>
            <Input
              id="clue"
              value={clue}
              onChange={(e) => setClue(e.target.value)}
              placeholder="T.ex. VARM ÅRSTID"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loading || !word.trim() || !clue.trim()}
            className="w-full"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Skickar...
              </>
            ) : (
              'Skicka förslag'
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
